// ─── App Store Links ──────────────────────────────────────────────────────────
// Klaro Money is live and free on the App Store and Google Play.
export const storeUrls = {
  ios: 'https://apps.apple.com/in/app/klaro-money/id6767815991',
  android: 'https://play.google.com/store/apps/details?id=com.klar.mobile',
};

// ─── Navigation ─────────────────────────────────────────────────────────────
export const navItems = [
  { label: 'Home',           to: '/' },
  { label: 'Product',        to: '/product' },
  { label: 'Student Journey', to: '/student-journey' },
  { label: 'Contact',        to: '/contact' },
];

// ─── Budget Categories ───────────────────────────────────────────────────────
export const categories = [
  {
    name: 'Mess',
    emoji: '🍱',
    color: '#C8F135',
    colorDim: 'rgba(200,241,53,0.12)',
    defaultBudget: 3000,
    example: 'Monthly mess fees, tiffin subscriptions',
    description: 'Your primary daily meals — mess, tiffin, or hostel dining.',
  },
  {
    name: 'Food Outside',
    emoji: '🍕',
    color: '#F97316',
    colorDim: 'rgba(249,115,22,0.12)',
    defaultBudget: 1500,
    example: 'Swiggy, Zomato, canteen, chai',
    description: 'Treats, deliveries, and food runs outside your mess.',
  },
  {
    name: 'Transport',
    emoji: '🚌',
    color: '#3B82F6',
    colorDim: 'rgba(59,130,246,0.12)',
    defaultBudget: 800,
    example: 'Bus pass, auto, Ola/Uber, metro',
    description: 'Getting around — autos, buses, metro, or ride-shares.',
  },
  {
    name: 'Hostel',
    emoji: '🏠',
    color: '#8B5CF6',
    colorDim: 'rgba(139,92,246,0.12)',
    defaultBudget: 2000,
    example: 'Room fees, electricity, laundry',
    description: 'Room rent, electricity bills, laundry, and hostel extras.',
  },
  {
    name: 'Personal Care',
    emoji: '🧴',
    color: '#14B8A6',
    colorDim: 'rgba(20,184,166,0.12)',
    defaultBudget: 600,
    example: 'Toiletries, haircut, medicines',
    description: 'Grooming, healthcare, and personal hygiene essentials.',
  },
  {
    name: 'Entertainment',
    emoji: '🎮',
    color: '#EC4899',
    colorDim: 'rgba(236,72,153,0.12)',
    defaultBudget: 800,
    example: 'OTT, movies, outings, games',
    description: 'Movies, OTT subscriptions, games, and fun outings.',
  },
  {
    name: 'Savings',
    emoji: '🏦',
    color: '#10B981',
    colorDim: 'rgba(16,185,129,0.12)',
    defaultBudget: 1000,
    example: 'Emergency fund, goals, UPI wallet',
    description: 'Money you intentionally set aside — future you will be grateful.',
  },
  {
    name: 'Misc',
    emoji: '📦',
    color: '#9090A8',
    colorDim: 'rgba(144,144,168,0.12)',
    defaultBudget: 300,
    example: 'Stationery, gifts, unexpected',
    description: 'The catch-all for everything that doesn\'t fit elsewhere.',
  },
];

// ─── Features ────────────────────────────────────────────────────────────────
export const features = [
  {
    id: 'budget',
    icon: 'budget',
    title: 'Smart Budget Allocation',
    desc: 'Set a monthly budget and split it across 8 real student spending categories. Klar remembers your split every month.',
  },
  {
    id: 'expense',
    icon: 'expense',
    title: 'Tap-to-Log Expenses',
    desc: 'A custom numpad lets you log any expense in under 5 seconds. No forms, no friction — just tap and move on.',
  },
  {
    id: 'income',
    icon: 'income',
    title: 'Income & Cash Flow',
    desc: 'Log pocket money, stipends, and side-hustle income alongside your spending to see your real cash flow at a glance.',
  },
  {
    id: 'goals',
    icon: 'goals',
    title: 'Savings Goals',
    desc: 'Set savings goals — a trip, a gadget, an emergency fund — and watch your progress build month after month.',
  },
  {
    id: 'reminders',
    icon: 'reminders',
    title: 'Recurring Bill Reminders',
    desc: 'Add recurring bills like OTT, rent, or subscriptions and get reminders so a payment never slips through the cracks.',
  },
  {
    id: 'insights',
    icon: 'insights',
    title: 'Insights & Daily Streak',
    desc: 'Day, week, and month trend charts show where your money goes, while a daily streak keeps the logging habit alive.',
  },
  {
    id: 'lock',
    icon: 'lock',
    title: 'App Lock & Privacy',
    desc: 'Lock Klar behind Face ID, fingerprint, or a PIN. Your data lives on your device — no ads, no selling, ever.',
  },
  {
    id: 'export',
    icon: 'export',
    title: 'Search & CSV Export',
    desc: 'Search and filter any transaction instantly, and export your full history to CSV whenever you want your data.',
  },
];

// ─── Steps (How it works) ────────────────────────────────────────────────────
export const steps = [
  {
    number: '1',
    title: 'Sign In with Google',
    desc: 'Open the app and sign in with your Google account in one tap. Tell Klar your name and phone number — that\'s it.',
  },
  {
    number: '2',
    title: 'Set Your Monthly Budget',
    desc: 'Enter your total monthly budget, then allocate amounts to each of the 8 spending categories. Takes 2 minutes.',
  },
  {
    number: '3',
    title: 'Log & Learn Daily',
    desc: 'Every time you spend, open Klar, pick a category, and log the amount. Watch your donut chart update in real time.',
  },
];

// ─── Testimonials ────────────────────────────────────────────────────────────
export const testimonials = [
  {
    name: 'Priya Raghavan',
    role: 'B.Tech 2nd year, IIT Madras',
    initials: 'PR',
    avatarColor: '#C8F135',
    quote: 'I had no idea I was spending ₹2,400 a month on food outside. Klar showed me the donut chart after week one and I genuinely could not believe it. I\'ve saved ₹800 every month since.',
    stars: 5,
  },
  {
    name: 'Arjun Mehta',
    role: 'MBA 1st year, IIM Kozhikode',
    initials: 'AM',
    avatarColor: '#8B5CF6',
    quote: 'The numpad is insanely fast. I log expenses in the mess queue before I\'ve even picked up my plate. Streak is at 23 days — don\'t want to break it now.',
    stars: 5,
  },
  {
    name: 'Fatima Siddiqui',
    role: 'B.Com 3rd year, Pune University',
    initials: 'FS',
    avatarColor: '#14B8A6',
    quote: 'Finally an app that actually gets the hosteller life. Mess, transport, the random chai outside — all covered. No subscription, no ads. Recommended to my entire floor.',
    stars: 5,
  },
];

// ─── FAQ (Home page – 5 questions) ──────────────────────────────────────────
export const homeFAQ = [
  {
    q: 'Is Klar really free forever?',
    a: 'Yes. Klar is 100% free with no ads, no premium tier, and no hidden charges. We believe every student deserves a proper budgeting tool regardless of their financial situation. "₹0 forever" isn\'t a marketing line — it\'s the product model.',
  },
  {
    q: 'Does Klar access my bank account or UPI?',
    a: 'No. Klar does not connect to your bank, read your UPI history, or scrape SMS messages. You manually log every expense. This means your banking credentials and transaction history stay completely private.',
  },
  {
    q: 'Where is my data stored?',
    a: 'Your budget, expenses, and settings live on your device. You can lock the app behind Face ID, a fingerprint, or a PIN, and export your full history to CSV anytime you want a copy of your data.',
  },
  {
    q: 'Who is Klar designed for?',
    a: 'Klar is built specifically for Indian college students — both hostellers and day scholars. The 8 default categories reflect real student spending patterns: mess food, outside food, transport, hostel costs, personal care, entertainment, savings, and miscellaneous.',
  },
  {
    q: 'Is Klar available on iPhone and Android?',
    a: 'Yes. Klar (Klaro Money) is live and free on both the App Store — iPhone, iPad, and Mac — and on Google Play for Android. Just search "Klaro Money" or tap a download button on this page.',
  },
];

// ─── FAQ (Student Journey page – 8 questions) ────────────────────────────────
export const journeyFAQ = [
  {
    q: 'Do I need to log every single expense?',
    a: 'Yes, and that\'s intentional. The habit of consciously noting every expense — even a ₹20 chai — is what builds financial awareness. The numpad is designed to make this take under 5 seconds so it never feels like a chore.',
  },
  {
    q: 'What if I forget to log something?',
    a: 'It happens. Just log it when you remember. Accuracy matters more than perfect timing. Your streak will reset if you miss a day, but your budget data stays intact.',
  },
  {
    q: 'Can I change my budget mid-month?',
    a: 'Yes. You can update your monthly budget and category allocations anytime. Klar will recalculate your remaining balance based on what you\'ve already logged.',
  },
  {
    q: 'What does the donut ring chart show?',
    a: 'The home screen donut chart shows your total spending broken down by category. Each slice represents a category — you can see at a glance which area is consuming the most of your budget.',
  },
  {
    q: 'How does the daily streak work?',
    a: 'Your streak counts consecutive days where you\'ve logged at least one expense. It resets to zero if you miss a full day. The streak is visible on the home screen as a motivational signal.',
  },
  {
    q: 'Can I use Klar if I don\'t live in a hostel?',
    a: 'Absolutely. Day scholars use Klar just as effectively. The Hostel category can be repurposed for commute costs or home expenses, and every category amount is customizable to your situation.',
  },
  {
    q: 'Is Klar available on iOS?',
    a: 'Yes. Klar (Klaro Money) is live and free on the App Store for iPhone, iPad, and Mac, and on Google Play for Android. Download it on whichever device you carry to class.',
  },
  {
    q: 'Will Klar always be free?',
    a: 'Yes. The decision to keep Klar free forever is a core product value, not a business strategy. College students shouldn\'t have to pay to learn how to manage money. We mean it.',
  },
];
